import type { CSSProperties } from 'react';
import type { KanbanColumnStatus } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';

/**
 * Visual tokens aligned with OMP PMP Kanban mockup.
 * Soft badge fills + emerald primary actions.
 */
export const WORK_ORDER_UI = {
  primary: '#10b981',
  primaryHover: '#059669',
  sidebarNavy: '#0f172a',
  pageBg: '#f5f7fa',
  cardBorder: '#e2e8f0',
  cardBg: '#ffffff',
  suggestionLink: '#2563eb',
  columnLaneBg: '#f1f5f9',
  priority: {
    HIGH: { bg: '#fee2e2', fg: '#991b1b' },
    MEDIUM: { bg: '#ffedd5', fg: '#9a3412' },
    LOW: { bg: '#dbeafe', fg: '#1e40af' },
  },
  /** Tinted header chips — separate from the scrollable WO lane (mockup). */
  columnHeader: {
    PLANNED: { bg: '#dbeafe', fg: '#1e3a8a', badge: '#3b82f6' },
    ASSIGNED: { bg: '#fef3c7', fg: '#92400e', badge: '#f59e0b' },
    IN_PROGRESS: { bg: '#d1fae5', fg: '#065f46', badge: '#10b981' },
    ON_HOLD: { bg: '#ede9fe', fg: '#5b21b6', badge: '#8b5cf6' },
    COMPLETED: { bg: '#e2e8f0', fg: '#334155', badge: '#64748b' },
    CLOSED: { bg: '#ccfbf1', fg: '#115e59', badge: '#0f766e' },
  },
  status: {
    IN_PROGRESS: { bg: '#dcfce7', fg: '#166534' },
    ON_HOLD: { bg: '#f3e8ff', fg: '#5b21b6' },
    PLANNED: { bg: '#e0f2fe', fg: '#075985' },
    ASSIGNED: { bg: '#fef3c7', fg: '#92400e' },
    COMPLETED: { bg: '#e2e8f0', fg: '#334155' },
  },
  ticketType: {
    PREVENTIVE: { bg: '#dbeafe', fg: '#1e40af' },
    CURATIVE: { bg: '#fee2e2', fg: '#991b1b' },
  },
  /** Ticket lifecycle status — distinct colors for at-a-glance Kanban scanning. */
  ticketStatus: {
    OPEN: { bg: '#dbeafe', fg: '#1e40af', accent: '#3b82f6' },
    IN_PROGRESS: { bg: '#d1fae5', fg: '#065f46', accent: '#10b981' },
    CLOSED: { bg: '#e2e8f0', fg: '#334155', accent: '#64748b' },
    CANCELLED: { bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444' },
  },
  /** Latest visit execution result — high-contrast chips (antd "success" is too pale). */
  executionResult: {
    COMPLETED_OK: { bg: '#166534', fg: '#ffffff' },
    COMPLETED_WITH_ISSUE: { bg: '#c2410c', fg: '#ffffff' },
    NOT_DONE: { bg: '#475569', fg: '#ffffff' },
    CANCELLED: { bg: '#b91c1c', fg: '#ffffff' },
  },
} as const;

export type SoftBadgeTone = { bg: string; fg: string };

export type TicketStatusTone = SoftBadgeTone & { accent: string };

export type ColumnHeaderTone = {
  bg: string;
  fg: string;
  badge: string;
};

export function columnHeaderTone(status: KanbanColumnStatus): ColumnHeaderTone {
  return (
    WORK_ORDER_UI.columnHeader[
      status as keyof typeof WORK_ORDER_UI.columnHeader
    ] ?? WORK_ORDER_UI.columnHeader.PLANNED
  );
}

export function ticketStatusTone(status: string): TicketStatusTone {
  return (
    WORK_ORDER_UI.ticketStatus[
      status as keyof typeof WORK_ORDER_UI.ticketStatus
    ] ?? WORK_ORDER_UI.ticketStatus.OPEN
  );
}

export function softBadgeStyle(tone: SoftBadgeTone): CSSProperties {
  return {
    background: tone.bg,
    color: tone.fg,
    border: 'none',
    borderRadius: 6,
    fontWeight: 500,
  };
}

export function executionResultTone(result: string): SoftBadgeTone {
  return (
    WORK_ORDER_UI.executionResult[
      result as keyof typeof WORK_ORDER_UI.executionResult
    ] ?? WORK_ORDER_UI.executionResult.NOT_DONE
  );
}

/** Keeps page chrome + column titles visible; cards scroll inside each lane. */
export const KANBAN_BOARD_HEIGHT = 'calc(100vh - 240px)';
export const KANBAN_COLUMN_MIN_WIDTH = 300;
